import type { DelayBroadcastGranularity, DelayBroadcastPattern } from '../../../api/types';
import {
  DELAY_BROADCAST_GRANULARITIES,
  GRANULARITY_DEFAULT_SSML,
} from '../../../utils/broadcastContents';
// 数値入力は共通の NumericInput を使う。
// 素の <input type="number" value={number}> だと Number('') === 0 のため
// 既定値 0 を消せない不具合が起きる (NumericInput 側のコメント参照)。
import { clampNonNegativeInt, NumericInput } from '../../NumericInput';

export function DelayPatternRow({
  index,
  pattern,
  canRemove,
  ssmlTestIdPrefix = 'self-train-delay-ssml-template',
  placeholderTemplates = GRANULARITY_DEFAULT_SSML,
  onUpdate,
  onAdd,
  onRemove,
}: {
  index: number;
  pattern: DelayBroadcastPattern;
  /** 削除可能か (最後の 1 行のときは false。false のとき「−」ボタンを表示しない) */
  canRemove: boolean;
  /**
   * SSML テンプレート <textarea> に付与する data-testid のプレフィックス。
   * 自列車遅延と接続線遅延 (transfer_aware) の両方で同じ行コンポーネントを再利用するため、
   * 呼び出し元で区別できるように `<prefix>-<index>` の testid が割り当てられる。
   * デフォルトは自列車遅延の従来 testid (`self-train-delay-ssml-template-{index}`) を維持する。
   */
  ssmlTestIdPrefix?: string;
  /**
   * 放送粒度ごとのデフォルト SSML 文言 (textarea の placeholder に使われる)。
   * 自列車遅延と接続線遅延 (transfer_aware) で文面が異なるため、呼び出し元から
   * 注入できるようにする。デフォルトは自列車遅延の文言
   * (`GRANULARITY_DEFAULT_SSML` / Lambda の `GRANULARITY_DEFAULT_TEMPLATE` と一致)。
   * 接続線遅延の表形式エディタからは `CONNECTING_GRANULARITY_DEFAULT_SSML` を渡し、
   * Lambda の `CONNECTING_GRANULARITY_DEFAULT_TEMPLATE` と一致した
   * 「<駅名>駅からお乗換の<線名>... 現在 N 分ほど遅れています」系の文面を表示する。
   */
  placeholderTemplates?: Record<DelayBroadcastGranularity, string>;
  onUpdate: (patch: Partial<DelayBroadcastPattern>) => void;
  onAdd: () => void;
  onRemove: () => void;
}): React.JSX.Element {
  const granularity = pattern.granularity as DelayBroadcastGranularity;

  return (
    <tr style={{ verticalAlign: 'top', borderTop: '1px solid #ddd' }}>
      {/* 遅れ分数 */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
          {/*
            遅れ開始分数は NumericInput を使う。
            素の <input type="number" value={number}> だと Number('') === 0 のため
            既定値 0 を消せない (詳細は NumericInput のコメント参照)。
          */}
          <NumericInput
            min={0}
            step={1}
            style={{ width: 90 }}
            aria-label={`パターン${index + 1} 遅れ開始分数`}
            value={pattern.delay_minutes_from}
            normalizeValue={clampNonNegativeInt}
            onChange={(delayMinutesFrom) => onUpdate({ delay_minutes_from: delayMinutesFrom })}
          />
          <span>分以上〜</span>
          {/*
            遅れ終了分数は「空欄 = 上限なし (undefined)」を正規の値として扱うため、
            NumericInput ではなく素の input を使う (空欄が意味を持つので 0 に戻さない)。
          */}
          <input
            type="number"
            min={0}
            step={1}
            style={{ width: 90 }}
            aria-label={`パターン${index + 1} 遅れ終了分数`}
            value={pattern.delay_minutes_to ?? ''}
            placeholder="上限なし"
            onChange={(e) => {
              const val = e.target.value;
              onUpdate({
                delay_minutes_to: val === '' ? undefined : Math.max(0, Math.floor(Number(val))),
              });
            }}
          />

          <span>分未満の遅れ</span>
        </div>
      </td>

      {/* 放送粒度 */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
        <select
          aria-label={`パターン${index + 1} 放送粒度`}
          value={granularity}
          onChange={(e) => onUpdate({ granularity: e.target.value as DelayBroadcastGranularity })}
        >
          {DELAY_BROADCAST_GRANULARITIES.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </td>

      {/* 放送パターン (SSMLテンプレート) */}
      <td style={{ padding: '6px', width: '100%' }}>
        {/*
         * data-testid を付与する理由:
         * placeholder には粒度別のデフォルト SSML 文 (例: "<speak>...</speak>") が
         * 動的に入るため、page-summary が拾うラベル (placeholder 優先) が長文化し、
         * AI エージェントが基本情報の「説明」欄 (data-testid=description-input) と
         * 取り違える事象が報告された (Claude が長考の末「{誤った action} 申し訳ありません
         * {正しい action}」と自己訂正レスポンスを返す原因になる)。
         * 明示的な data-testid を付けることで page-summary でも安定したラベル
         * (`self-train-delay-ssml-template-{index}`) として現れ、E2E シナリオからも
         * data-testid 単独で一意に拾えるようにする。
         */}
        <textarea
          rows={2}
          style={{ width: '100%', boxSizing: 'border-box' }}
          aria-label={`パターン${index + 1} 放送パターン`}
          data-testid={`${ssmlTestIdPrefix}-${index}`}
          // ユーザ要望: 各 SSML 入力欄は必須項目にする。
          // (デフォルト文言を初期値としてプリセットしているため、削除→ゼロベース入力する
          //  場合も保存ボタンを押した瞬間にバリデーションで気付ける)
          required
          value={pattern.ssml_template ?? ''}
          placeholder={placeholderTemplates[granularity]}
          onChange={(e) =>
            // 空文字入力 (ユーザがクリアしてゼロベース入力に入った状態) もそのまま反映する。
            // ※ バリデーション (`validateContent`) は空文字をエラーとして検出する。
            onUpdate({ ssml_template: e.target.value })
          }
        />
      </td>

      {/* 操作 (行の追加・削除) */}
      <td style={{ padding: '6px', whiteSpace: 'nowrap', textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button
            type="button"
            className="delay-pattern-add"
            aria-label={`パターン${index + 1} の後に行を追加`}
            title="行を追加"
            onClick={onAdd}
          >
            ＋
          </button>
          {canRemove ? (
            <button
              type="button"
              className="delay-pattern-remove danger"
              aria-label={`パターン${index + 1} を削除`}
              title="行を削除"
              onClick={onRemove}
            >
              −
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
